<script lang="ts" setup>
import AIconArrowSmall from '~/components/atoms/icons/ArrowRightSmall.vue'
import MImageLoader from '@/components/molecules/forms/ImageLoader.vue'

const emit = defineEmits<{
  (e: 'image-selected', file: File): void
}>()

const changeImage = (file: File): void => {
  emit('image-selected', file)
}

</script>

<template>
  <div class="instruction">
    <div class="container-lg">
      <div class="row">
        <div class="col-12 col-lg-4">
          <article class="instruction__item mb-40">
            <div class="instruction__item_dual-img">
              <NuxtImg
                format="webp"
                src="/steps/steps-plate-with-bg1.webp"
                alt="Фотография тарелки с фоном"
                class="instruction__img-dual"
                sizes="220px 991px:145px 1199px:172px 1399px:202px"
                fetchpriority="low"
                loading="lazy"
                width="520"
                height="604"
                style="height: auto; aspect-ratio: 520 / 604;"
              />
              <NuxtImg
                format="webp"
                src="/steps/steps-plate-wo-bg1.webp"
                alt="Фотография тарелки без фона"
                class="instruction__img-dual"
                sizes="220px 991px:145px 1199px:172px 1399px:202px"
                fetchpriority="low"
                loading="lazy"
                width="520"
                height="604"
                style="height: auto; aspect-ratio: 520 / 604;"
              />
              <AIconArrowSmall />
            </div>
            <h5 class="fw-bold mb-1">
              1. Загрузите фото для карточки товара
            </h5>
            <p class="mb-0">
              Magvi удалит фон с фотографии онлайн
            </p>
            <MImageLoader
              v-slot="scope"
              @change="changeImage"
            >
              <button
                @click="scope.loadImage"
                class="d-flex align-items-center btn btn-link p-0 custom-link btn-arrow"
              >
                Загрузить
                <BootstrapIconChevronRight class="ms-1" />
              </button>
            </MImageLoader>
          </article>
        </div>
        <div class="col-12 col-lg-4">
          <article class="instruction__item mb-40">
            <NuxtImg
              format="webp"
              src="/steps/interface-choose-design.webp"
              alt="Интерфейс выбора дизайна"
              class="instruction__img"
              sizes="437px 479px:457px 991px:294px 1199px:354px 1399px:414px"
              fetchpriority="low"
              loading="lazy"
              width="1060"
              height="604"
              style="height: auto; aspect-ratio: 1060 / 604;"
            />
            <h5 class="fw-bold mb-1">
              2. Выберите дизайн из готовых шаблонов
            </h5>
            <p class="mb-0">
              Сервис предложит более десятка шаблонов для изображений с фоном и без
            </p>
          </article>
        </div>
        <div class="col-12 col-lg-4">
          <article class="instruction__item mb-40">
            <NuxtImg
              format="webp"
              src="/steps/interface-generation-bg.webp"
              alt="Интерфейс генерации фона"
              class="instruction__img"
              sizes="437px 479px:457px 991px:294px 1199px:354px 1399px:414px"
              fetchpriority="low"
              loading="lazy"
              width="1060"
              height="604"
              style="height: auto; aspect-ratio: 1060 / 604;"
            />
            <h5 class="fw-bold mb-1">
              3. Сгенерируйте фон при помощи нейросети
            </h5>
            <p class="mb-0">
              Исскуственный интеллект разместит ваш товар на десятках реалистичных фонов
            </p>
          </article>
        </div>
        <div class="col-12 col-lg-4 offset-lg-2">
          <article class="instruction__item mb-40 mb-lg-0">
            <NuxtImg
              format="webp"
              src="/steps/interface-editor.webp"
              alt="Интерфейс редактирования инфографики для маркетплейсов"
              class="instruction__img"
              sizes="437px 479px:457px 991px:294px 1199px:354px 1399px:414px"
              fetchpriority="low"
              loading="lazy"
              width="1060"
              height="604"
              style="height: auto; aspect-ratio: 1060 / 604;"
            />
            <h5 class="fw-bold mb-1">
              4. Измените элементы карточки
            </h5>
            <p class="mb-0">
              Добавьте иконки и текст, выберите шрифт, настройте цвет и размер объектов
            </p>
          </article>
        </div>
        <div class="col-12 col-lg-4">
          <article class="instruction__item">
            <NuxtImg
              format="webp"
              src="/steps/interface-with-our-cards.webp"
              alt="Интерфейс с вашими карточками с инфографикой для маркетплейсов"
              class="instruction__img"
              sizes="437px 479px:457px 991px:294px 1199px:354px 1399px:414px"
              fetchpriority="low"
              loading="lazy"
              width="712"
              height="407"
              style="height: auto; aspect-ratio: 1060 / 604;"
            />
            <h5 class="fw-bold mb-1">
              5. Создавайте свои шаблоны
            </h5>
            <p class="mb-0">
              Сохраняйте карточки в личном кабинете и экспериментируйте для роста продаж
            </p>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.instruction {
  padding: rem(70px) 0;

  @include media-breakpoint-down(lg) {
    padding: rem(60px) 0;
  }

  &__item {
    @include media-breakpoint-down(lg) {
      width: calc(50% - 24px);
    }

    @include media-breakpoint-down(md) {
      width: calc(100% - 16px);
    }

    @media (max-width: 374px) {
      width: 100%;
    }

    &_dual-img {
      display: flex;
      position: relative;
      gap: rem(10px);
      max-width: rem(530px);
      aspect-ratio: 530 / 302;
      margin-bottom: rem(24px);

      @include media-breakpoint-up(lg) {
        margin-bottom: rem(12px);
      }

      img {
        border: 1px solid $gray-300;
        border-radius: rem(4px);
        width: 49.058%;
      }

      svg {
        position: absolute;
        width: 39px;
        height: 17px;
        top: 16px;
        left: calc(50% - 14px);
        color: $indigo-500;

        @include media-breakpoint-down(lg) {
          transform: rotate(180deg) scaleX(-1);
          width: 35px;
          height: 14px;
          top: 98%;
          left: calc(50% - 17px);
        }
      }
    }
  }

  &__img {
    width: 100%;
    max-width: rem(530px);
    margin-bottom: rem(20px);
    border: 1px solid $gray-300;
    border-radius: rem(4px);

    @media (min-width: 375px) {
      margin-bottom: rem(24px);
    }

    @include media-breakpoint-up(lg) {
      margin-bottom: rem(20px);
    }
  }
}
</style>
